import React, { useState, useEffect, useMemo, useRef } from 'react';
import { User, Mail, Phone, Award, Calendar, Clock, DollarSign, ArrowLeft, ArrowRight, CalendarCheck, CalendarX, Banknote } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { format, addDays } from 'date-fns';
import { formatTime } from '../../utils/clock';
import { getAvailableSlots, getDoctorAvailableSlots, bookAppointment } from '../../redux/appointmentSlice';
import { getSingleCustomPrice } from '../../redux/customPriceSlice';
import { getPatientById } from '../../redux/patientSlice';
import AppointmentModal from './AppointmentModal';
import PatientAppointmentModal from './PatientAppointmentModal';
import CommonLoader from '../CommonLoader';
import ModalShell from '../common/ModalShell';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { getFileURL } from '../../utils/fileURL';
import { useMasterLabel } from '../../hooks/useMasterLabel';
import { emptyDayLabel, emptyDayHint } from '../../utils/slotDayReason';
import { doctorCurrency, formatPrice } from '../../utils/currency';

// Per-row translated <option> for the specialty dropdown. Form value stays the
// specialty NAME so the existing logic that matches by `spec.name === selectedSpeciality`
// keeps working — only the visible label is localised via the master translation cache.
const SpecialityTranslatedOption = ({ name, doctor }) => {
    const specialtyId = doctor?.appointmentSpeciality?.find((s) => s.name === name)?.specialtyId;
    const label = useMasterLabel('masterSpecialty', specialtyId, 'name', name);
    return <option value={name}>{label}</option>;
};

const BookAppointmentModal = ({ isOpen, onClose, doctor, loading, onAppointmentBooked }) => {
    const { t } = useTranslation('bookappointmentmodal');
    const dispatch = useDispatch();
    const { selectedPatient } = useSelector((state) => state.patient);
    const userRole = localStorage.getItem('Role');
    // Use different state keys based on user role
    const { availableSlots, availableDoctorSlots, isLoading: appointmentLoading } = useSelector((state) => state.appointment);
    const { selectedCustomPrice } = useSelector((state) => state.customPrice);

    // Get the correct slots based on user role
    const slots = userRole === 'patient' ? availableSlots : availableDoctorSlots;

    const [selectedSpeciality, setSelectedSpeciality] = useState('');
    const [selectedAddressId, setSelectedAddressId] = useState('');
    const [selectedAppointmentType, setSelectedAppointmentType] = useState('');
    const [weekOffset, setWeekOffset] = useState(0);
    const [openDay, setOpenDay] = useState(null);
    const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [rescheduleRecommendations, setRescheduleRecommendations] = useState([]);
    const [dropdownErrors, setDropdownErrors] = useState({
        speciality: '',
        address: '',
        appointmentType: ''
    });

    const systemTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const patientId = localStorage.getItem('ID');

    // Use useMemo to stabilize the today date
    const today = useMemo(() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), now.getDate());
    }, []);

    // Use ref to track last API call params to prevent duplicate calls
    const lastApiCallRef = useRef(null);

    // Helper function to get the Monday of the current week
    const getCurrentWeekMonday = (date) => {
        const day = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
        const diff = date.getDate() - day + (day === 0 ? -6 : 1);
        return new Date(date.getFullYear(), date.getMonth(), diff);
    };

    // Get patient data - only once when modal opens
    useEffect(() => {
        if (isOpen && patientId && !selectedPatient) {
            dispatch(getPatientById(patientId));
        }
    }, [isOpen, patientId, dispatch, selectedPatient]);

    // Validate dropdowns
    const validateDropdowns = () => {
        const errors = {
            speciality: '',
            address: '',
            appointmentType: ''
        };

        if (!selectedSpeciality) {
            errors.speciality = t('bookappointmentmodal.specialty_required');
        }

        if (!selectedAddressId) {
            errors.address = t('bookappointmentmodal.address_required');
        }

        if (!selectedAppointmentType) {
            errors.appointmentType = t('bookappointmentmodal.appointment_type_required');
        }

        setDropdownErrors(errors);
        return Object.values(errors).every(error => !error);
    };

    // Reset selections when modal closes
    useEffect(() => {
        if (!isOpen) {
            setSelectedSpeciality('');
            setSelectedAddressId('');
            setSelectedAppointmentType('');
            setWeekOffset(0);
            setOpenDay(null);
            setSelectedSlot(null);
            setIsBookingModalOpen(false);
            setDropdownErrors({ speciality: '', address: '', appointmentType: '' });
            lastApiCallRef.current = null;
        }
    }, [isOpen]);

    // Memoize computed values to prevent unnecessary recalculations (must be before useEffects that use them)
    const specialities = useMemo(() =>
        doctor?.appointmentSpeciality?.map(spec => spec.name) || [],
        [doctor?.appointmentSpeciality]
    );

    const addressTypes = useMemo(() =>
        doctor?.address?.filter(addr =>
            addr.specialty?.some(spec => spec.name === selectedSpeciality)
        ).map(addr => ({
            id: addr.addressId,
            type: addr.addressType
        })) || [],
        [doctor?.address, selectedSpeciality]
    );

    const appointmentTypes = useMemo(() =>
        doctor?.appointmentSpeciality?.find(
            spec => spec.name === selectedSpeciality
        )?.appointmentTypes?.map(type => type.type) || [],
        [doctor?.appointmentSpeciality, selectedSpeciality]
    );

    const selectedAppointmentDetails = useMemo(() =>
        doctor?.appointmentSpeciality
            ?.find(spec => spec.name === selectedSpeciality)
            ?.appointmentTypes?.find(type => type.type === selectedAppointmentType),
        [doctor?.appointmentSpeciality, selectedSpeciality, selectedAppointmentType]
    );

    // Extract typeId for stable dependency
    const appointmentTypeId = useMemo(() =>
        selectedAppointmentDetails?.typeId || null,
        [selectedAppointmentDetails?.typeId]
    );

    // Fetch available slots when selections change - with duplicate prevention
    useEffect(() => {
        if (!isOpen) return;

        const doctorId = doctor?.doctorId;
        if (!doctorId || !selectedSpeciality || !selectedAddressId || !selectedAppointmentType || !appointmentTypeId) {
            return;
        }

        const currentWeekMonday = getCurrentWeekMonday(today);
        const startDate = format(addDays(currentWeekMonday, weekOffset * 7), 'yyyy-MM-dd');
        const endDate = format(addDays(new Date(startDate), 6), 'yyyy-MM-dd');

        // Create a unique key for this API call
        const apiCallKey = `${doctorId}-${selectedAddressId}-${appointmentTypeId}-${startDate}-${endDate}`;

        // Only call API if parameters have changed
        if (lastApiCallRef.current !== apiCallKey) {
            lastApiCallRef.current = apiCallKey;

            // Use different API based on user role
            if (userRole === 'patient') {
                dispatch(getAvailableSlots({
                    doctorId,
                    startDate,
                    endDate,
                    timeZone: systemTimeZone,
                    addressType: selectedAddressId,
                    appointmentTypeId,
                    isReschedulingProcess: true
                }));
            } else {
                // For doctor or admin
                dispatch(getDoctorAvailableSlots({
                    doctorId,
                    startDate,
                    endDate,
                    timeZone: systemTimeZone,
                    addressType: selectedAddressId,
                    appointmentTypeId,
                    isReschedulingProcess: true
                }));
            }
        }
    }, [isOpen, doctor?.doctorId, selectedSpeciality, selectedAddressId, selectedAppointmentType, appointmentTypeId, weekOffset, dispatch, systemTimeZone, today, userRole]);

    // Fetch custom price - with duplicate prevention
    const lastPriceCallRef = useRef(null);
    useEffect(() => {
        if (!isOpen) return;

        if (doctor?.doctorId && selectedSpeciality && selectedAppointmentType && patientId) {
            const specialityId = selectedAppointmentDetails?.specialtyId;
            const appointmentTypeIdForPrice = selectedAppointmentDetails?.typeId;

            if (patientId && doctor.doctorId && specialityId && appointmentTypeIdForPrice) {
                const priceCallKey = `${patientId}-${doctor.doctorId}-${specialityId}-${appointmentTypeIdForPrice}`;

                // Only call API if parameters have changed
                if (lastPriceCallRef.current !== priceCallKey) {
                    lastPriceCallRef.current = priceCallKey;
                    dispatch(getSingleCustomPrice({
                        patientId,
                        doctorId: doctor.doctorId,
                        specialityId,
                        appointmentTypeId: appointmentTypeIdForPrice
                    }));
                }
            }
        }
    }, [isOpen, doctor?.doctorId, selectedSpeciality, selectedAppointmentType, selectedAppointmentDetails, patientId, dispatch]);

    const availableDays = Object.entries(slots || {})
        .map(([day, data]) => ({ day, ...data }))
        .sort((a, b) => new Date(a.date) - new Date(b.date));

    // Every day of the week refused for the same reason — the doctor has no
    // active WEEK RULE covering it. Nothing in this app used to mention week
    // rules at all, so the calendar just looked broken (B7).
    const weekNotOpen = availableDays.length > 0 && availableDays.every((d) => d.reason === 'closed');

    const currentWeekMonday = getCurrentWeekMonday(today);
    const startDate = addDays(currentWeekMonday, weekOffset * 7);
    const endDate = addDays(startDate, 6);

    const handleNextWeek = () => {
        setWeekOffset(prev => Math.min(2, prev + 1));
        setOpenDay(null);
    };

    const handlePrevWeek = () => {
        setWeekOffset(prev => Math.max(0, prev - 1));
        setOpenDay(null);
    };

    const toggleDayDrawer = (day) => {
        // Validate dropdowns before opening day drawer
        if (openDay !== day && !validateDropdowns()) {
            return;
        }
        setOpenDay(openDay === day ? null : day);
    };

    const handleSlotClick = (slot) => {
        // Validate dropdowns before opening booking modal
        if (!validateDropdowns()) {
            return;
        }

        setSelectedSlot({
            start: slot.start,
            end: new Date(new Date(slot.start).getTime() + (selectedAppointmentDetails?.duration
                ? parseInt(selectedAppointmentDetails.duration.split(':')[0]) * 60 * 60 * 1000 +
                parseInt(selectedAppointmentDetails.duration.split(':')[1]) * 60 * 1000
                : 60 * 60 * 1000)).toISOString()
        });
        setIsBookingModalOpen(true);
    };

    const formatDurationToMinutes = (timeStr) => {
        if (!timeStr || !timeStr.includes(":")) return timeStr;
        const [hours, minutes] = timeStr.split(":").map(Number);
        const totalMinutes = (hours * 60) + minutes;
        return `${totalMinutes} ${t('bookappointmentmodal.mins')}`;
    };

    // The fee is priced in the doctor's currency, read by utils/currency.js
    // (P24) — the country's currency code first. This modal used to match the
    // country NAME and the time zone itself and pick a rupee or a euro icon, so
    // every doctor outside those two countries was shown a euro icon. The icon
    // is neutral now and the sign travels with the amount; a currency nobody
    // names leaves the amount bare.
    const feeText = () => {
        const custom = selectedCustomPrice?.customPrice;
        const fee = custom !== undefined && custom !== null ? custom : (selectedAppointmentDetails?.price || null);
        return formatPrice(fee, doctorCurrency(doctor), { fallback: t('bookappointmentmodal.na') });
    };

    if (!isOpen) return null;

    return (
        <>
            {isOpen && (
                // Click on the dimmed page closes, as before the shared frame.
                <div onClick={(e) => { if (e.target === e.currentTarget.firstElementChild) onClose(); }}>
                    <ModalShell
                        title={<span data-testid="book-appointment-modal-title">{t('bookappointmentmodal.title')}</span>}
                        onClose={onClose}
                        size="max-w-6xl"
                        testId="book-appointment-modal"
                        closeTestId="book-appointment-modal-close-button"
                        contentTestId="book-appointment-modal-content"
                    >
                        <div className="min-h-0" data-testid="book-appointment-modal-body">
                            {loading ? (
                                <div className="flex flex-col items-center justify-center py-12" data-testid="book-appointment-modal-loading">
                                    <CommonLoader />
                                    <span className="text-xl text-[#4ABDBD] font-semibold mt-4" data-testid="book-appointment-modal-loading-text">
                                        {t('bookappointmentmodal.loading_doctor_details')}
                                    </span>
                                </div>
                            ) : doctor ? (
                                <div className="space-y-6" data-testid="book-appointment-modal-data">
                                    {/* Doctor Info */}
                                    {userRole === 'patient' && (<div className="bg-gradient-to-br from-[#4ABDBD]/5 to-[#3A9B9B]/5 border border-[#4ABDBD]/20 rounded-2xl p-4 shadow-sm" data-testid="book-appointment-doctor-info-section">
                                        <div className="flex items-center gap-4">
                                            {doctor.image && (
                                                <img
                                                    src={getFileURL(doctor.image)}
                                                    alt={`Dr. ${doctor.name} ${doctor.surname}`}
                                                    className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-md"
                                                    data-testid="book-appointment-doctor-image"
                                                />
                                            )}
                                            <div>
                                                <h3 className="text-lg font-bold text-gray-800" data-testid="book-appointment-doctor-name">
                                                    {t('bookappointmentmodal.dr_prefix')} {doctor.name} {doctor.surname}
                                                </h3>
                                                {doctor.email && (
                                                    <p className="text-sm text-gray-600 flex items-center gap-1" data-testid="book-appointment-doctor-email">
                                                        <Mail className="w-3.5 h-3.5" data-testid="book-appointment-doctor-email-icon" />
                                                        {doctor.email}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>)}

                                    {/* Selection Dropdowns */}
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="book-appointment-dropdowns-container">
                                        <div data-testid="book-appointment-specialty-field-container">
                                            <label className="block text-sm font-medium text-gray-700 mb-2" data-testid="book-appointment-specialty-label">
                                                {t('bookappointmentmodal.specialty_label')} <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                value={selectedSpeciality}
                                                onChange={(e) => {
                                                    setSelectedSpeciality(e.target.value);
                                                    setSelectedAddressId('');
                                                    setSelectedAppointmentType('');
                                                    setWeekOffset(0);
                                                    setOpenDay(null);
                                                    // Clear error when selection is made
                                                    setDropdownErrors(prev => ({ ...prev, speciality: '' }));
                                                }}
                                                className={`tt-select w-full p-3 border rounded-xl bg-white text-gray-700 focus:ring-2 focus:ring-[#4ABDBD] focus:outline-none ${dropdownErrors.speciality
                                                        ? 'border-red-300 bg-red-50'
                                                        : 'border-gray-200'
                                                    }`}
                                                data-testid="book-appointment-specialty-select"
                                            >
                                                <option value="">{t('bookappointmentmodal.select_specialty', 'Select Specialty')}</option>
                                                {specialities.map((spec) => (
                                                    <SpecialityTranslatedOption key={spec} name={spec} doctor={doctor} />
                                                ))}
                                            </select>
                                            {dropdownErrors.speciality && (
                                                <p className="text-red-600 text-xs mt-1" data-testid="book-appointment-specialty-error">{dropdownErrors.speciality}</p>
                                            )}
                                        </div>

                                        <div data-testid="book-appointment-address-field-container">
                                            <label className="block text-sm font-medium text-gray-700 mb-2" data-testid="book-appointment-address-label">
                                                {t('bookappointmentmodal.address_label')} <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                value={selectedAddressId}
                                                onChange={(e) => {
                                                    setSelectedAddressId(e.target.value);
                                                    setSelectedAppointmentType('');
                                                    setWeekOffset(0);
                                                    setOpenDay(null);
                                                    // Clear error when selection is made
                                                    setDropdownErrors(prev => ({ ...prev, address: '' }));
                                                }}
                                                className={`tt-select w-full p-3 border rounded-xl bg-white text-gray-700 focus:ring-2 focus:ring-[#4ABDBD] focus:outline-none ${dropdownErrors.address
                                                        ? 'border-red-300 bg-red-50'
                                                        : 'border-gray-200'
                                                    }`}
                                                disabled={!selectedSpeciality}
                                                data-testid="book-appointment-address-select"
                                            >
                                                <option value="">{t('bookappointmentmodal.select_address', 'Select Address')}</option>
                                                {addressTypes.map((addr) => (
                                                    <option key={addr.id} value={addr.id} data-testid={`book-appointment-address-option-${addr.id}`}>{addr.type}</option>
                                                ))}
                                            </select>
                                            {dropdownErrors.address && (
                                                <p className="text-red-600 text-xs mt-1" data-testid="book-appointment-address-error">{dropdownErrors.address}</p>
                                            )}
                                        </div>

                                        <div data-testid="book-appointment-type-field-container">
                                            <label className="block text-sm font-medium text-gray-700 mb-2" data-testid="book-appointment-type-label">
                                                {t('bookappointmentmodal.appointment_type_label')} <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                value={selectedAppointmentType}
                                                onChange={(e) => {
                                                    setSelectedAppointmentType(e.target.value);
                                                    setWeekOffset(0);
                                                    setOpenDay(null);
                                                    // Clear error when selection is made
                                                    setDropdownErrors(prev => ({ ...prev, appointmentType: '' }));
                                                }}
                                                className={`tt-select w-full p-3 border rounded-xl bg-white text-gray-700 focus:ring-2 focus:ring-[#4ABDBD] focus:outline-none ${dropdownErrors.appointmentType
                                                        ? 'border-red-300 bg-red-50'
                                                        : 'border-gray-200'
                                                    }`}
                                                disabled={!selectedSpeciality || !selectedAddressId}
                                                data-testid="book-appointment-type-select"
                                            >
                                                <option value="">{t('bookappointmentmodal.select_type', 'Select Type')}</option>
                                                {appointmentTypes.map((type) => (
                                                    <option key={type} value={type} data-testid={`book-appointment-type-option-${type}`}>{type}</option>
                                                ))}
                                            </select>
                                            {dropdownErrors.appointmentType && (
                                                <p className="text-red-600 text-xs mt-1" data-testid="book-appointment-type-error">{dropdownErrors.appointmentType}</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Appointment Details */}
                                    {selectedAppointmentType && selectedAppointmentDetails && (
                                        <div className="flex gap-4" data-testid="book-appointment-details-container">
                                            <div className="flex gap-3 items-center px-4 py-2 bg-white shadow-sm border border-[#4ABDBD]/20 rounded-xl" data-testid="book-appointment-price-card">
                                                <span className="w-9 h-9 flex items-center justify-center rounded-full bg-[#4ABDBD]/15 text-[#3A9B9B]">
                                                    <Banknote size={18} data-testid="book-appointment-price-icon" />
                                                </span>
                                                <div>
                                                    <p className="text-xs text-gray-400" data-testid="book-appointment-price-label">{t('bookappointmentmodal.price_label')}</p>
                                                    <p className="text-sm font-semibold text-gray-700" data-testid="book-appointment-price-value">
                                                        {feeText()}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="flex gap-3 items-center px-4 py-2 bg-white shadow-sm border border-[#4ABDBD]/20 rounded-xl" data-testid="book-appointment-duration-card">
                                                <span className="w-9 h-9 flex items-center justify-center rounded-full bg-[#4ABDBD]/15 text-[#3A9B9B]">
                                                    <Clock size={18} data-testid="book-appointment-duration-icon" />
                                                </span>
                                                <div>
                                                    <p className="text-xs text-gray-400" data-testid="book-appointment-duration-label">{t('bookappointmentmodal.duration_label')}</p>
                                                    <p className="text-sm font-semibold text-gray-700" data-testid="book-appointment-duration-value">
                                                        {formatDurationToMinutes(selectedAppointmentDetails.duration) || t('bookappointmentmodal.na')}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Available Slots */}
                                    {selectedAppointmentType && (
                                        <div data-testid="book-appointment-slots-section">
                                            {appointmentLoading ? (
                                                <div className="flex flex-col items-center justify-center py-8" data-testid="book-appointment-slots-loading">
                                                    <CommonLoader />
                                                    <span className="text-[#4ABDBD] font-semibold mt-4" data-testid="book-appointment-slots-loading-text">{t('bookappointmentmodal.loading_available_slots')}</span>
                                                </div>
                                            ) : availableDays.length > 0 ? (
                                                <div data-testid="book-appointment-slots-container">
                                                    <div className="flex items-center justify-center gap-4 mb-6" data-testid="book-appointment-week-navigation">
                                                        <button
                                                            onClick={handlePrevWeek}
                                                            disabled={weekOffset === 0}
                                                            className="p-2 bg-[#4ABDBD] text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#3AA8A8] transition-all"
                                                            data-testid="book-appointment-prev-week-button"
                                                        >
                                                            <ArrowLeft size={20} data-testid="book-appointment-prev-week-icon" />
                                                        </button>
                                                        <h3 className="text-lg font-semibold text-gray-800" data-testid="book-appointment-week-range-text">
                                                            {format(startDate, 'MMM d, yyyy')} - {format(endDate, 'MMM d, yyyy')}
                                                        </h3>
                                                        <button
                                                            onClick={handleNextWeek}
                                                            disabled={weekOffset === 2}
                                                            className="p-2 bg-[#4ABDBD] text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#3AA8A8] transition-all"
                                                            data-testid="book-appointment-next-week-button"
                                                        >
                                                            <ArrowRight size={20} data-testid="book-appointment-next-week-icon" />
                                                        </button>
                                                    </div>

                                                    {weekNotOpen && (
                                                        <div
                                                            data-testid="book-appointment-week-not-open"
                                                            className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
                                                        >
                                                            {t('bookappointmentmodal.week_not_open', 'This week is not open for booking — the doctor has no active week rule covering it. Open the week under the doctor’s availability, or page to another week.')}
                                                        </div>
                                                    )}

                                                    <div className="grid grid-cols-1 gap-4" data-testid="book-appointment-days-grid">
                                                        {availableDays.map(({ day, date, slots, reason }) => {
                                                            const hasAvailableSlots = slots?.filter(slot => slot.status === 'available').length > 0;
                                                            // WHY the day is empty, straight from the API's per-day `reason`
                                                            // ('closed' = the doctor has not opened that week yet). Without it
                                                            // every cause reads as one flat "No slots available".
                                                            const emptyLabel = hasAvailableSlots ? '' : emptyDayLabel(reason, date, t);
                                                            const emptyHint = hasAvailableSlots ? '' : emptyDayHint(reason, t);
                                                            return (
                                                                <div
                                                                    key={day}
                                                                    className="group overflow-hidden rounded-xl border border-gray-200 bg-gray-50 shadow-sm hover:shadow-md transition-all"
                                                                    data-testid={`book-appointment-day-card-${day}`}
                                                                >
                                                                    <div
                                                                        className={`p-4 bg-gray-50 flex items-center gap-3 ${hasAvailableSlots ? 'cursor-pointer hover:bg-gray-100' : 'cursor-default'} transition-all`}
                                                                        onClick={hasAvailableSlots ? () => toggleDayDrawer(day) : undefined}
                                                                        data-testid={`book-appointment-day-toggle-${day}`}
                                                                    >
                                                                        {hasAvailableSlots ? (
                                                                            <CalendarCheck size={20} className="text-[#4ABDBD]" data-testid={`book-appointment-day-available-icon-${day}`} />
                                                                        ) : (
                                                                            <CalendarX size={20} className="text-gray-400" data-testid={`book-appointment-day-unavailable-icon-${day}`} />
                                                                        )}
                                                                        <h4 className="font-semibold text-gray-500 group-hover:text-[#4ABDBD] transition-colors" data-testid={`book-appointment-day-label-${day}`}>
                                                                            {day}, {format(new Date(date), 'MMM d, yyyy')}
                                                                        </h4>
                                                                        {!hasAvailableSlots && (
                                                                            <span
                                                                                className="text-sm text-gray-500 ml-auto"
                                                                                title={emptyHint || undefined}
                                                                                data-slot-reason={reason || 'unknown'}
                                                                                data-testid={`book-appointment-day-no-slots-${day}`}
                                                                            >
                                                                                {emptyLabel}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    {hasAvailableSlots && openDay === day && (
                                                                        <div className="p-4 bg-white border-t border-gray-200" data-testid={`book-appointment-slots-drawer-${day}`}>
                                                                            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                                                                                {slots
                                                                                    .filter(slot => slot.status === 'available')
                                                                                    .map((slot, idx) => (
                                                                                        <div
                                                                                            key={idx}
                                                                                            className="p-2 bg-gray-50 border border-gray-200 rounded-xl text-center text-sm hover:bg-[#4ABDBD] hover:text-white cursor-pointer transition-all"
                                                                                            onClick={() => handleSlotClick(slot)}
                                                                                            data-testid={`book-appointment-slot-${day}-${idx}`}
                                                                                        >
                                                                                            {formatTime(slot.start)}
                                                                                        </div>
                                                                                    ))}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="text-center py-8" data-testid="book-appointment-no-slots-container">
                                                    <p className="text-gray-500" data-testid="book-appointment-no-slots-text">{t('bookappointmentmodal.no_available_slots')}</p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-12" data-testid="book-appointment-no-data-container">
                                    <p className="text-gray-500 text-lg" data-testid="book-appointment-no-data-text">{t('bookappointmentmodal.no_doctor_data')}</p>
                                </div>
                            )}
                        </div>
                    </ModalShell>
                </div>
            )}

            {/* Appointment Modal for Booking - Conditional based on user role */}
            {isBookingModalOpen && selectedSlot && selectedPatient && (
                userRole === 'doctor' ? (
                    // Use AppointmentModal for doctors
                    <AppointmentModal
                        isModalOpen={isBookingModalOpen}
                        setIsModalOpen={(open) => {
                            setIsBookingModalOpen(open);
                            if (!open) {
                                setSelectedSlot(null);
                            }
                        }}
                        selectedSlot={selectedSlot}
                        setSelectedSlot={setSelectedSlot}
                        selectedAppointment={null}
                        setSelectedAppointment={() => { }}
                        isRescheduling={false}
                        setIsRescheduling={() => { }}
                        appointments={[]}
                        setAppointments={() => { }}
                        selectedDoctor={doctor}
                        onReschedule={() => { }}
                        isModalOpenAppointment={false}
                        setisModalOpenAppointment={() => { }}
                        setAppoId={() => { }}
                        dateRangeStart={format(startDate, 'yyyy-MM-dd')}
                        dateRangeEnd={format(endDate, 'yyyy-MM-dd')}
                        initialSpeciality={selectedSpeciality}
                        initialAppointmentType={selectedAppointmentType}
                        initialAddressId={selectedAddressId}
                        onAppointmentBooked={() => {
                            // Close the AppointmentModal
                            setIsBookingModalOpen(false);
                            setSelectedSlot(null);
                            // Call the parent callback to close BookAppointmentModal and refresh library
                            if (onAppointmentBooked) {
                                onAppointmentBooked();
                            }
                        }}
                    />
                ) : (
                    // Use PatientAppointmentModal for patients
                    <PatientAppointmentModal
                        isModalOpen={isBookingModalOpen}
                        setIsModalOpen={(open) => {
                            setIsBookingModalOpen(open);
                            if (!open) {
                                setSelectedSlot(null);
                            }
                        }}
                        selectedSlot={selectedSlot}
                        setSelectedSlot={setSelectedSlot}
                        selectedDoctor={doctor}
                        selectedPatient={selectedPatient}
                        selectedSpeciality={selectedSpeciality}
                        selectedAddressId={selectedAddressId}
                        selectedAppointmentType={selectedAppointmentType}
                        startDate={startDate}
                        endDate={endDate}
                        isRescheduling={false}
                        onRescheduleConfirm={() => { }}
                        rescheduleData={null}
                        selectedCustomPrice={selectedCustomPrice}
                        onAppointmentBooked={() => {
                            // Close the PatientAppointmentModal
                            setIsBookingModalOpen(false);
                            setSelectedSlot(null);
                            // Call the parent callback to close BookAppointmentModal and refresh library
                            if (onAppointmentBooked) {
                                onAppointmentBooked();
                            }
                        }}
                    />
                )
            )}
        </>
    );
};

export default BookAppointmentModal;

